import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Sparkles, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getSessionId } from "@/lib/session";
import { trackEvent } from "@/lib/audit";
import { awardPoints } from "@/lib/gamification";
import {
  decideMatchPulse,
  getMatchState,
  markAnswered,
  markClosed,
  markShown,
  type MatchPulseQuestion,
} from "@/lib/polls/matchPulse";

export interface MatchPulseProps {
  matchId: string;
  minute: number;
  firstGoalMinute: number | null;
  /** epoch ms when the first goal happened — used to compute 15s delay. */
  firstGoalAt?: number | null;
  mvpCandidates?: string[];
  /** Optional auth user id; falls back to session id for throttling. */
  userId?: string | null;
  country?: string | null;
  team?: string | null;
  /** Nation code del usuario (Mundial). Se persiste en fan_data_responses.nation. */
  nation?: string | null;
  /** Phase 3 — Fan Data Pulse In-Game gating. */
  activeForMs?: number;
  suppressed?: boolean;
  /** Match status: when "FT" (or anything not LIVE/HT) the pulse closes. */
  matchStatus?: "LIVE" | "HT" | "FT" | string;
  /** Render as a normal in-flow card instead of a floating overlay. */
  inline?: boolean;
}

/**
 * Match Pulse™ — mini encuesta discreta esquina inferior derecha durante el partido.
 *
 * Reglas: máx 3 por partido, 1 por mitad, no se vuelve a mostrar si el usuario cierra.
 * No cubre marcador / Prediction Engine / Radar / Audio Sync — vive en su propia
 * esquina con `pointer-events: auto` solo sobre la tarjeta.
 */
export function MatchPulse({
  matchId, minute, firstGoalMinute, firstGoalAt, mvpCandidates,
  userId, country, team, nation, activeForMs, suppressed, matchStatus, inline = false,
}: MatchPulseProps) {
  const sessionId = useMemo(() => getSessionId(), []);
  const stateKey = userId ?? sessionId;

  const [question, setQuestion] = useState<MatchPulseQuestion | null>(null);
  const [expanded, setExpanded] = useState(false);
  // PR-MOBILE-OVERLAY — On phones the mini-card was covering MatchStats rows.
  // Default to a tiny collapsed pill on < sm; tap to reveal the mini card.
  const [mobileOpen, setMobileOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const shownRef = useRef<string | null>(null);

  const secondsSinceFirstGoal = firstGoalAt ? Math.floor((Date.now() - firstGoalAt) / 1000) : null;

  // PR-UX-VIRAL-01 — Auto-close any open pulse (incl. MVP) when the
  // match ends. The final whistle belongs to the user, not to sponsors.
  useEffect(() => {
    if (matchStatus && matchStatus !== "LIVE" && matchStatus !== "HT") {
      if (question) {
        markClosed(matchId, stateKey);
        void trackEvent("match_pulse_auto_closed" as never, {
          match_id: matchId, reason: "match_end", status: matchStatus,
        });
      }
      setQuestion(null);
      setExpanded(false);
      setCollapsed(true);
    }
  }, [matchStatus, matchId, stateKey, question]);

  useEffect(() => {
    if (done || collapsed) return;
    // No reabrir si ya está cerrada o se llegó al límite.
    const st = getMatchState(matchId, stateKey);
    if (st.closed || st.totalShown >= 3) return;
    if (st.cooldownUntil && Date.now() < st.cooldownUntil) return;
    const q = decideMatchPulse({
      matchId, userOrSession: stateKey, minute,
      firstGoalMinute, secondsSinceFirstGoal, mvpCandidates,
      activeForMs, suppressed,
    });
    if (q && shownRef.current !== q.id) {
      shownRef.current = q.id;
      setQuestion(q);
      setPicked([]);
      markShown(matchId, stateKey, q);
      void trackEvent("match_pulse_shown" as never, {
        match_id: matchId, trigger: q.trigger, minute: q.minute, sponsor: q.sponsor,
      });
    }
  }, [matchId, stateKey, minute, firstGoalMinute, secondsSinceFirstGoal, mvpCandidates, done, collapsed, activeForMs, suppressed]);

  const close = () => {
    if (question) {
      markClosed(matchId, stateKey);
      void trackEvent("match_pulse_closed" as never, {
        match_id: matchId, trigger: question.trigger, minute: question.minute,
      });
    }
    setQuestion(null);
    setExpanded(false);
  };

  const submit = async (answer: string | string[]) => {
    if (!question || submitting) return;
    setSubmitting(true);
    const isMulti = Array.isArray(answer);
    const answerStr = isMulti ? (answer as string[]).join(", ") : (answer as string);
    const answerJson = isMulti ? (answer as string[]) : null;
    try {
      await (supabase.from("fan_data_responses" as never) as unknown as {
        insert: (row: Record<string, unknown>) => Promise<{ error: unknown }>;
      }).insert({
        user_id: userId ?? null,
        session_id: sessionId,
        match_id: matchId,
        question_id: question.id,
        question_type: question.category,
        question: question.question,
        answer: answerStr,
        answer_options: answerJson,
        is_multi_select: isMulti,
        country: country ?? null,
        team: team ?? null,
        nation: nation ?? null,
        surface: "match_pulse",
        trigger: question.trigger,
        trigger_type: question.trigger,
        minute: question.minute,
        sponsor: question.sponsor ?? null,
      });
      markAnswered(matchId, stateKey, question.id);
      await awardPoints("match_pulse_answer", {
        trigger: question.trigger, minute: question.minute, sponsor: question.sponsor,
      });
      void trackEvent("match_pulse_answered" as never, {
        match_id: matchId, trigger: question.trigger, minute: question.minute, sponsor: question.sponsor,
      });
      setDone(true);
      window.setTimeout(() => { setDone(false); setQuestion(null); setExpanded(false); }, 3500);
    } catch (e) {
      console.warn("[MatchPulse] insert failed", e);
      setDone(true);
      window.setTimeout(() => { setDone(false); setQuestion(null); setExpanded(false); }, 2500);
    } finally {
      setSubmitting(false);
    }
  };

  const submitMulti = () => {
    if (picked.length === 0) return;
    void submit(picked);
  };
  const togglePick = (opt: string) => {
    // PR-UX-VIRAL-01 — Haptic feedback (iOS supported via WebKit since 16.4,
    // Android via Vibration API). Safe no-op when unsupported.
    try {
      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        navigator.vibrate(10);
      }
    } catch { /* noop */ }
    setPicked((prev) => (prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt]));
  };

  if (!question && !done) return null;

  return (
    <div
      className={
        inline
          ? "w-full"
          : "pointer-events-none fixed bottom-24 right-3 z-[60] w-[min(320px,calc(100vw-1.5rem))] sm:bottom-6 sm:right-6"
      }
      aria-live="polite"
    >
      {/* Mobile-only collapsed pill — does not cover MatchStats. */}
      {!inline && !done && !expanded && !mobileOpen && (
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="pointer-events-auto ml-auto flex items-center gap-1.5 rounded-full border border-border bg-background/95 px-3 py-1.5 text-[11px] font-black shadow-xl backdrop-blur-md sm:hidden"
          aria-label="Abrir encuesta rápida"
        >
          <span className="inline-block h-2 w-2 rounded-full bg-[oklch(0.82_0.24_145)]" />
          Encuesta · +5 XP
        </button>
      )}
      <div
        className={`pointer-events-auto rounded-2xl border border-border bg-background/95 p-3 shadow-2xl backdrop-blur-md ${
          !inline && !done && !expanded && !mobileOpen ? "hidden sm:block" : ""
        }`}
      >
        {done ? (
          <div className="flex items-center gap-2 text-[12px] font-bold text-[oklch(0.82_0.24_145)]">
            <CheckCircle2 className="h-4 w-4" /> Gracias. Has ganado 5 XP.
          </div>
        ) : !expanded ? (
          // ============ Mini ============
          <div>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[oklch(0.82_0.24_145)]">
                <span className="inline-block h-2 w-2 rounded-full bg-[oklch(0.82_0.24_145)]" />
                Encuesta rápida
                {question?.sponsor ? (
                  <span className="ml-1 rounded bg-foreground/10 px-1.5 py-[1px] text-[9px] text-foreground/80">
                    {question.sponsor}
                  </span>
                ) : null}
              </div>
              <button
                onClick={() => { setMobileOpen(false); close(); }}
                aria-label="Cerrar encuesta"
                className="rounded-full p-1 text-muted-foreground hover:bg-foreground/10"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="mt-1.5 text-[13px] font-black leading-snug">{question?.question}</div>
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={() => setExpanded(true)}
                className="flex-1 rounded-lg bg-[oklch(0.82_0.24_145)] px-3 py-1.5 text-[11px] font-black text-background hover:opacity-90"
              >
                Responder · +5 XP
              </button>
              <button
                onClick={() => { setMobileOpen(false); setCollapsed(true); setQuestion(null); }}
                className="rounded-lg border border-border px-2 py-1.5 text-[11px] font-bold hover:bg-foreground/5"
              >
                Luego
              </button>
            </div>
            <div className="mt-1.5 inline-flex items-center gap-1 text-[9px] text-muted-foreground">
              <Sparkles className="h-2.5 w-2.5" /> Min {question?.minute} · {question?.trigger.replace(/_/g, " ")}
            </div>
          </div>
        ) : (
          // ============ Expandido ============
          <div>
            <div className="flex items-start justify-between gap-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[oklch(0.82_0.24_145)]">
                Match Pulse {question?.sponsor ? `· ${question.sponsor}` : ""}
              </div>
              <button
                onClick={close}
                aria-label="Cerrar"
                className="rounded-full p-1 text-muted-foreground hover:bg-foreground/10"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="mt-1.5 text-sm font-black leading-snug">{question?.question}</div>
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              {question?.options.map((opt) => (
                question?.multiSelect ? (
                  <button
                    key={opt}
                    type="button"
                    disabled={submitting}
                    onClick={() => togglePick(opt)}
                    aria-pressed={picked.includes(opt)}
                    className={`rounded-lg border px-2 py-1.5 text-[11px] font-bold transition disabled:opacity-50 ${
                      picked.includes(opt)
                        ? "border-[oklch(0.82_0.24_145)] bg-[oklch(0.82_0.24_145)]/20 text-foreground"
                        : "border-border bg-background/40 hover:border-[oklch(0.82_0.24_145)]/60 hover:bg-[oklch(0.82_0.24_145)]/10"
                    }`}
                  >
                    {opt}
                  </button>
                ) : (
                  <button
                    key={opt}
                    disabled={submitting}
                    onClick={() => void submit(opt)}
                    className="rounded-lg border border-border bg-background/40 px-2 py-1.5 text-[11px] font-bold transition hover:border-[oklch(0.82_0.24_145)]/60 hover:bg-[oklch(0.82_0.24_145)]/10 disabled:opacity-50"
                  >
                    {opt}
                  </button>
                )
              ))}
            </div>
            {question?.multiSelect && (
              <button
                disabled={submitting || picked.length === 0}
                onClick={submitMulti}
                className="mt-2 w-full rounded-lg bg-[oklch(0.82_0.24_145)] px-3 py-1.5 text-[11px] font-black text-background hover:opacity-90 disabled:opacity-40"
              >
                Enviar ({picked.length}) · +5 XP
              </button>
            )}
            <div className="mt-2 text-[9px] text-muted-foreground">
              {question?.multiSelect ? "Elige una o varias · " : ""}+5 XP por responder · Min {question?.minute}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}